import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
    Activity,
    CheckCircle2,
    XCircle,
    Coins,
    TrendingUp,
    Bot,
    Workflow,
    Recycle,
} from "lucide-react";
import Sparkline from "@/components/Sparkline";
import NetworkOverview from "@/components/billing/network/NetworkOverview";
function Stat({ label, value, hint, icon: Icon, accent = false, testId }) {
    return (
        <div
            data-testid={testId}
            className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 flex flex-col justify-between min-h-[120px]"
        >
            <div className="flex items-start justify-between">
                <span className="label-xs">{label}</span>
                {Icon ? (
                    <Icon
                        className={`w-4 h-4 ${accent ? "text-[#7C3AED]" : "text-white/40"}`}
                        strokeWidth={1.5}
                    />
                ) : null}
            </div>
            <div>
                <div className="font-display text-3xl tracking-tight mt-4">{value}</div>
                {hint ? (
                    <div className="text-xs text-white/40 mt-1 font-mono">{hint}</div>
                ) : null}
            </div>
        </div>
    );
}

export default function DashboardPage() {
    const [usage, setUsage] = useState(null);
    const [spark, setSpark] = useState(null);
    const [reuse, setReuse] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const now = new Date();
        const from = new Date(now.getTime() - days * 86400 * 1000).toISOString();
        Promise.all([
            api.get(`/admin/billing/usage?date_from=${from}`).then((r) => r.data).catch(() => null),
            api.get(`/admin/billing/usage/sparkline?hours=${days * 24}&bucket=day`).then((r) => r.data).catch(() => null),
            api.get(`/admin/billing/reuse/summary?days=${days}&top_n=5`).then((r) => r.data).catch(() => null),
        ]).then(([u, s, r]) => {
            setUsage(u);
            setSpark(s);
            setReuse(r);
        }).finally(() => setLoading(false));
    }, [days]);

    const totals = useMemo(() => usage?.totals || {}, [usage]);
    const sparkPoints = spark?.points || [];

    const successRate = useMemo(() => {
        const calls = totals.calls || 0;
        if (!calls) return 0;
        return Math.round(((totals.completed || 0) / calls) * 100);
    }, [totals]);

    return (
        <div className="px-8 py-8" data-testid="dashboard-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">Analytics</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">
                        Orchestration Overview
                    </h1>
                    <p className="text-sm text-white/50 mt-2 max-w-xl">
                        Real-time visibility into AI agents + flows orchestrated through AIService
                        platform. Metrics across the last <span className="text-white">{days} days</span>.
                    </p>
                </div>
                <div className="flex items-center gap-1 p-1 bg-[#0a0a0a] border border-white/10 rounded-md">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            data-testid={`range-${d}d`}
                            className={`px-3 py-1.5 text-xs font-mono rounded-sm transition-colors ${
                                days === d
                                    ? "bg-white text-black"
                                    : "text-white/60 hover:text-white"
                            }`}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            {loading && <div className="text-xs text-white/40 font-mono mb-6">Loading analytics…</div>}

            <NetworkOverview days={days} />

            <div className="label-xs mb-3" data-testid="dash-local-heading">This server — agent &amp; flow runs</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <Stat
                    label="Total calls"
                    value={(totals.calls || 0).toLocaleString()}
                    hint={`${(totals.completed || 0).toLocaleString()} completed`}
                    icon={Activity}
                    testId="stat-calls"
                />
                <Stat
                    label="Success rate"
                    value={`${successRate}%`}
                    hint={`${totals.failed || 0} failed`}
                    icon={successRate >= 90 ? CheckCircle2 : XCircle}
                    accent={successRate >= 90}
                    testId="stat-success"
                />
                <Stat
                    label="Total cost"
                    value={`$${(totals.cost_usd || 0).toFixed(4)}`}
                    hint={`${(totals.tokens_in || 0).toLocaleString()} in / ${(totals.tokens_out || 0).toLocaleString()} out`}
                    icon={Coins}
                    accent
                    testId="stat-cost"
                />
                <Stat
                    label="Avg latency"
                    value={`${Math.round((totals.latency_ms || 0) / Math.max(1, totals.calls || 1))}ms`}
                    hint={`${(totals.latency_ms || 0).toLocaleString()}ms total`}
                    icon={TrendingUp}
                    testId="stat-latency"
                />
            </div>

            {/* Reuse-revenue tile — links to Billing for the per-tenant breakdown. */}
            <Link
                to="/billing#reuse"
                className="block bg-[#0a0a0a] border border-white/10 hover:border-[#7C3AED]/40 hover:bg-[#7C3AED]/5 transition-colors rounded-md p-5 mb-6"
                data-testid="dash-reuse-revenue-tile"
            >
                <div className="flex items-center justify-between gap-6 flex-wrap">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-md bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center">
                            <Recycle className="w-5 h-5 text-[#7C3AED]" strokeWidth={1.6} />
                        </div>
                        <div>
                            <div className="label-xs text-[#7C3AED]">Reuse revenue</div>
                            <div className="font-display text-2xl tracking-tight mt-1" data-testid="dash-reuse-revenue-total">
                                ${(reuse?.total_usd || 0).toFixed(4)}
                            </div>
                            <div className="text-[11px] font-mono text-white/40 mt-1">
                                {(reuse?.total_docs || 0).toLocaleString()} docs reused · {(reuse?.total_events || 0).toLocaleString()} events · last {days} days
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-mono text-white/50">
                        {Object.entries(reuse?.by_source || {}).slice(0, 4).map(([src, s]) => (
                            <div key={src} className="px-2.5 py-1 rounded-sm bg-white/[0.03] border border-white/10">
                                <span className="text-white/40 mr-1.5">{src.replace(/_/g, " ")}</span>
                                <span className="text-white">${(s?.total_usd || 0).toFixed(4)}</span>
                            </div>
                        ))}
                    </div>
                    <span className="text-[11px] font-mono text-[#7C3AED] hover:text-[#a17bff]">
                        View per-tenant breakdown →
                    </span>
                </div>
            </Link>

            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="col-span-2 bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid="dash-trend">
                    <div className="flex items-center justify-between mb-2">
                        <span className="label-xs">Call volume — last {days} days</span>
                        <span className="text-[11px] font-mono text-white/40">{sparkPoints.length} buckets</span>
                    </div>
                    <div className="mt-2">
                        <Sparkline points={sparkPoints} metric="calls" color="#7C3AED" height={120} width={600} />
                    </div>
                </div>
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid="dash-split">
                    <div className="label-xs mb-3">Call breakdown</div>
                    <div className="space-y-3">
                        <SplitRow icon={Workflow} label="Flows" calls={totals.flow_calls || 0} cost={totals.flow_cost_usd || 0} total={totals.calls || 1} tone="text-[#c7a6ff]" />
                        <SplitRow icon={Bot} label="Agents" calls={totals.agent_calls || 0} cost={totals.agent_cost_usd || 0} total={totals.calls || 1} tone="text-emerald-300" />
                    </div>
                </div>
            </div>

            {/* Quick links */}
            <div className="grid grid-cols-3 gap-3" data-testid="dash-quick-links">
                <QuickLink to="/agents" icon={Bot} title="AI Agents" description="Design prompts, evaluate versions, run playgrounds." />
                <QuickLink to="/flows" icon={Workflow} title="Flows" description="Compose agent + API nodes into orchestrated workflows." />
                <QuickLink to="/observability" icon={Activity} title="Observability" description="Walk full execution traces + share read-only links." />
            </div>
        </div>
    );
}

function SplitRow({ icon: Icon, label, calls, cost, total, tone }) {
    const pct = total ? Math.round((calls / total) * 100) : 0;
    return (
        <div>
            <div className="flex items-center justify-between">
                <span className={`flex items-center gap-1.5 text-xs font-mono ${tone}`}>
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {label}
                </span>
                <span className="text-xs font-mono text-white/80">{calls.toLocaleString()} · ${cost.toFixed(5)}</span>
            </div>
            <div className="w-full h-1.5 bg-white/5 rounded-sm mt-1.5 overflow-hidden">
                <div
                    className="h-full bg-[#7C3AED]"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function QuickLink({ to, icon: Icon, title, description }) {
    return (
        <Link to={to} className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 hover:border-[#7C3AED]/40 hover:bg-[#7C3AED]/5 transition-colors group">
            <Icon className="w-5 h-5 text-[#7C3AED] group-hover:text-[#a17bff]" strokeWidth={1.5} />
            <div className="font-display text-base mt-3">{title}</div>
            <div className="text-xs text-white/50 mt-1">{description}</div>
        </Link>
    );
}
